import React from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { CITY_DATA, getLocalizedContent } from '../data/cityData';
import { SERVICES_CONFIG } from '../data/servicesConfig';

const CityServicePage = () => {
  const { serviceSlug, citySlug } = useParams();
  const service = SERVICES_CONFIG[serviceSlug];
  const city = CITY_DATA[citySlug];

  if (!service || !city) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-4xl font-bold mb-4">Page Not Found</h1>
        <Link to="/services" className="text-[#3a87fe] hover:underline">View All Services</Link>
      </div>
    );
  }

  const localContent = getLocalizedContent(citySlug, service.name);

  const seoData = {
    title: `${service.name} ${city.name} CT | ${service.shortDesc} near ${city.landmarks[0]} | ${city.zip}`,
    description: `Professional ${service.name.toLowerCase()} in ${city.name}, Connecticut. Serving homeowners near ${city.landmarks.slice(0, 3).join(', ')}. Licensed contractor in ${city.name} ${city.zip}. Free estimates.`,
    keywords: `${service.name} ${city.name} CT, ${service.name.toLowerCase()} near ${city.landmarks[0]}, ${city.name} ${service.name.toLowerCase()} contractor, ${city.zip}`,
    canonical: `https://cthomeremodels.com/services/${service.slug}/${citySlug}`,
    ogImage: `https://cthomeremodels.com/og/v2/city-${citySlug}.png`,
    schemaData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": `${service.name} in ${city.name}, Connecticut`,
      "provider": {
        "@type": "GeneralContractor",
        "name": "Visser General Construction LLC",
        "url": "https://cthomeremodels.com",
        "telephone": "+1-203-604-3202",
        "address": { "@type": "PostalAddress", "streetAddress": "42 Gay St", "addressLocality": "Meriden", "addressRegion": "CT", "postalCode": "06450", "addressCountry": "US" }
      },
      "areaServed": { "@type": "City", "name": city.name, "containedInPlace": { "@type": "State", "name": "Connecticut" } },
      "description": `Professional ${service.name.toLowerCase()} services in ${city.name}, CT. Licensed & insured contractor.`,
      "url": `https://cthomeremodels.com/services/${service.slug}/${citySlug}`
    }
  };

  return (
    <div className="city-service-page">
      <SEO {...seoData} />

      {/* Hero */}
      <section
        className="relative py-32 md:py-40 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.5)), url('${service.heroImage}')`
        }}
        data-testid="city-service-hero"
      >
        <div className="absolute top-6 left-6 z-10">
          <img
            src="/api/media/name/visser-general-construction-llc-logo-connecticut.png"
            alt={`${service.name} Contractor ${city.name} Connecticut`}
            className="h-20 md:h-28 object-contain drop-shadow-2xl"
          />
        </div>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white drop-shadow-lg leading-tight">
            {service.name} in {city.name}, CT
          </h1>
          <p className="text-base md:text-lg max-w-4xl mx-auto text-white drop-shadow-md">
            Professional {service.name.toLowerCase()} contractor serving {city.name}, Connecticut.
            Trusted by homeowners near {city.landmarks.slice(0, 2).join(' and ')}.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Intro */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <img
                src={service.image1}
                alt={`${service.name} project in ${city.name} Connecticut near ${city.landmarks[0]}`}
                className="w-full h-80 object-cover rounded-xl shadow-lg"
                loading="lazy"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-4">{service.name} Services in {city.name}, Connecticut</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {localContent.introText}
              </p>
              <p className="text-gray-700 leading-relaxed">
                Whether you live {city.searchTerms[0]}, in {city.neighborhoods[0]}, or anywhere in {city.name},
                our {service.name.toLowerCase()} team delivers exceptional results. We understand {city.name}'s
                unique architectural styles and building requirements.
              </p>
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="bg-[#f4efe9] rounded-xl p-8 mb-12">
            <h2 className="text-3xl font-bold mb-6 text-center text-[#5c0701]">
              Why {city.name} Residents Choose Visser Construction for {service.name}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-lg mb-3">Local {city.name} Expertise</h3>
                <p className="text-gray-700 leading-relaxed">
                  As a {city.history}, {city.name} has unique characteristics. We're familiar with neighborhoods including {city.neighborhoods.join(', ')}, and we understand local building codes.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-3">Serving {city.name} Landmarks</h3>
                <p className="text-gray-700 leading-relaxed">
                  We've completed {service.name.toLowerCase()} projects throughout {city.name}, from homes near {city.landmarks[0]} to properties near {city.destinations[0]}.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-3">Quality Craftsmanship</h3>
                <p className="text-gray-700 leading-relaxed">
                  {service.description} Our {city.name} {service.name.toLowerCase()} services meet the highest standards.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-3">Licensed & Insured in {city.name} CT</h3>
                <p className="text-gray-700 leading-relaxed">
                  Fully licensed and insured to work in {city.name}, Connecticut. We handle all permits and inspections required by local building departments.
                </p>
              </div>
            </div>
          </div>

          {/* Neighborhoods */}
          <div className="bg-white border-l-4 border-[#3a87fe] p-8 mb-12">
            <h2 className="text-3xl font-bold mb-6">{city.name} Neighborhoods We Serve</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-gray-700">
              {city.neighborhoods.map((neighborhood, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#3a87fe]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  <span>{neighborhood}, {city.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Landmarks */}
          <div className="bg-gray-50 rounded-xl p-8 mb-12">
            <h2 className="text-3xl font-bold mb-6 text-center">{service.name} Near {city.name} Landmarks</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {city.landmarks.map((landmark, idx) => (
                <div key={idx} className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-bold text-lg mb-2 text-[#5c0701]">{service.name} near {landmark}</h3>
                  <p className="text-gray-600 text-sm">
                    Professional {service.name.toLowerCase()} for homes near {landmark} in {city.name}, CT {city.zip}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Project Photos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <img src={service.image2} alt={`${service.name} ${city.name} CT completed project`} className="w-full h-64 object-cover rounded-lg shadow-md" loading="lazy" />
            <img src={service.image3} alt={`${city.name} Connecticut ${service.name.toLowerCase()}`} className="w-full h-64 object-cover rounded-lg shadow-md" loading="lazy" />
            <img src={service.image4} alt={`Professional ${service.name.toLowerCase()} contractor ${city.name} CT`} className="w-full h-64 object-cover rounded-lg shadow-md" loading="lazy" />
          </div>

          {/* CTA */}
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Start Your {city.name} {service.name} Project?</h2>
            <p className="text-base md:text-lg text-gray-700 mb-8">
              Contact Visser Construction today for a free {service.name.toLowerCase()} estimate in {city.name}, Connecticut
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-[#3a87fe] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#2968d9] transition"
                data-testid="city-service-cta-contact"
              >
                Get Free {city.name} Estimate
              </Link>
              <a
                href="tel:2036043202"
                className="border-2 border-[#5c0701] text-[#5c0701] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#5c0701] hover:text-white transition"
                data-testid="city-service-cta-call"
              >
                Call (203) 604-3202
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityServicePage;
